// src/pages/TipsListPage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Input,
  Label,
  Form,
  Spinner,
  FormFeedback,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Alert
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import classnames from 'classnames';
import TableContainer from '../../Components/Common/TableContainer';
import {
  selectAllTips,
  selectTipsLoading,
  selectTipsError,
} from '../../slices/tip/list/reducer';
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { fetchUserTips } from "../../slices/tip/list/thunk";
import { authUserManager } from "../../helpers/api_helper";
import { updateEvent } from "../../slices/event/details/thunk";
import { getUserProfile, updateUserProfile } from "../../slices/profile/thunk";
import { resetProfileFlags } from "../../slices/profile/reducer";

const TipsListPage = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const tips = useSelector(selectAllTips);
  const loading = useSelector(selectTipsLoading);
  const error = useSelector(selectTipsError);
  const event = useSelector((state) => state.Event.event);
  const eventId = authUserManager.getAdminEventId();
  const userId = authUserManager.getAdminId();

  const profileUser = useSelector((state) => state.AdminProfile.user);
  const profileLoading = useSelector((state) => state.AdminProfile.loading);
  const profileUpdateSuccess = useSelector((state) => state.AdminProfile.updateSuccess);

  const [activeTab, setActiveTab] = useState('1');

  const [selectedYear, setSelectedYear] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');

  const availableYears = useMemo(() => {
    const years = new Set();
    tips.forEach((tip) => {
      if (tip.createdAt) {
        years.add(new Date(tip.createdAt).getFullYear());
      }
    });
    years.add(new Date().getFullYear());
    return Array.from(years).sort((a, b) => b - a);
  }, [tips]);

  const monthOptions = useMemo(() => {
    const lang = i18n.language || 'pl';
    return Array.from({ length: 12 }, (_, i) => ({
      value: String(i + 1),
      label: new Date(2000, i, 1).toLocaleString(lang, { month: 'long' }),
    }));
  }, [i18n.language]);

  const filteredTips = useMemo(() => {
    if (!selectedYear && !selectedMonth) return tips;
    return tips.filter((tip) => {
      if (!tip.createdAt) return false;
      const d = new Date(tip.createdAt);
      if (selectedYear && d.getFullYear() !== Number(selectedYear)) return false;
      if (selectedMonth && d.getMonth() + 1 !== Number(selectedMonth)) return false;
      return true;
    });
  }, [tips, selectedYear, selectedMonth]);

  const totalAmount = useMemo(
    () => filteredTips.reduce((sum, t) => sum + t.amount, 0),
    [filteredTips]
  );
  const paidAmount = useMemo(
    () => filteredTips.filter((t) => t.status === 1).reduce((sum, t) => sum + t.amount, 0),
    [filteredTips]
  );
  const unpaidAmount = useMemo(
    () => filteredTips.filter((t) => t.status === 0).reduce((sum, t) => sum + t.amount, 0),
    [filteredTips]
  );

  useEffect(() => {
    dispatch(fetchUserTips());
    if (userId) {
      dispatch(getUserProfile(userId));
    }
  }, [dispatch, userId]);

  useEffect(() => {
    if (profileUpdateSuccess) {
      setTimeout(() => dispatch(resetProfileFlags()), 3000);
    }
  }, [profileUpdateSuccess, dispatch]);

  const payoutForm = useFormik({
    enableReinitialize: true,
    initialValues: {
      purchaser: profileUser?.purchaser || '',
      bankAccount: profileUser?.bankAccount || '',
      phoneNumber: profileUser?.phoneNumber || '',
    },
    validationSchema: Yup.object({
      bankAccount: Yup.string()
        .matches(/^(PL)?\d{26}$/, t('Invalid bank account format')),
      phoneNumber: Yup.string()
        .matches(/^(\+?\d[\d\s-]{7,19})?$/, t('Invalid phone number format')),
    }),
    onSubmit: (values) => {
      dispatch(updateUserProfile(userId, values));
    },
  });

  const isBankSetFilled = () =>
    !!payoutForm.values.purchaser && !!payoutForm.values.bankAccount;

  const isBlikSetFilled = () => !!payoutForm.values.phoneNumber;

  const isBankSetSaved = () =>
    !!profileUser?.purchaser && !!profileUser?.bankAccount;

  const isBlikSetSaved = () => !!profileUser?.phoneNumber;

  const arePayoutFieldsFilled = () => isBankSetFilled() || isBlikSetFilled();

  const arePayoutFieldsSaved = () => isBankSetSaved() || isBlikSetSaved();

  const handleAllowTipsChange = (e) => {
    if (e.target.checked && (!arePayoutFieldsSaved() || !arePayoutFieldsFilled())) {
      return;
    }
    dispatch(updateEvent(eventId, { allowTips: e.target.checked }));
  };

  const toggleTab = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  };

  // Formatowanie kwoty z groszy na złote (jeśli amount jest w groszach, możesz to tu przeliczyć)
  const formatAmount = (amountInCents) => {
    return `${amountInCents} PLN`;
  };

  // Formatowanie daty
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pl-PL', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const columns = useMemo(
    () => [
      {
        header: t('tips.table.id'),
        cell: (cell) => {
          return (
            <Link to="#" className="fw-medium">
              #{cell.getValue()}
            </Link>
          );
        },
        accessorKey: "id",
        enableColumnFilter: false,
      },
      {
        header: t('tips.table.username'),
        accessorKey: "username",
        enableColumnFilter: false,
      },
      {
        header: t('tips.table.date'),
        accessorKey: "createdAt",
        enableColumnFilter: false,
        cell: (cell) => formatDate(cell.getValue()),
      },
      {
        header: t('tips.table.amount'),
        accessorKey: "amount",
        enableColumnFilter: false,
        cell: (cell) => (
          <span className="fw-semibold">{formatAmount(cell.getValue())}</span>
        ),
      },
      {
        header: t('tips.table.status'),
        enableColumnFilter: false,
        accessorKey: "status",
        cell: (cell) => {
          const isPaid = cell.getValue() === 1;
          return (
            <span className={`badge ${isPaid ? 'bg-success-subtle text-success' : 'bg-warning-subtle text-warning'} text-uppercase`}>
              {isPaid ? t('tips.status.paid') : t('tips.status.unpaid')}
            </span>
          );
        },
      },
    ],
    [t]
  );

  return (
    <React.Fragment>
      <div className="page-content non-admin-layout">
        <Container fluid>
          <BreadCrumb
            title={t('tips.breadcrumb.title')}
            pageTitle={t('Event View')}
            link={'/admin/event/' + eventId}
          />
          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader>
                  <Nav className="nav-tabs-custom rounded card-header-tabs border-bottom-0 nav-success" role="tablist">
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === '1' })}
                        onClick={() => toggleTab('1')}
                        style={{ cursor: 'pointer' }}
                      >
                        <i className="ri-money-dollar-circle-line me-1"></i>
                        {t('tips.header.title')}
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === '2' })}
                        onClick={() => toggleTab('2')}
                        style={{ cursor: 'pointer' }}
                      >
                        <i className="ri-settings-3-line me-1"></i>
                        {t('Settings')}
                      </NavLink>
                    </NavItem>
                  </Nav>
                </CardHeader>
                <CardBody>
                  <TabContent activeTab={activeTab}>
                    <TabPane tabId="1">
                      {!event?.allowTips && (
                        <Alert color="warning" className="d-flex align-items-center mb-2">
                          <i className="ri-information-line me-2 fs-5"></i>
                          <span>
                            {t('Tips are disabled. You can enable them in the Settings tab.')}
                          </span>
                        </Alert>
                      )}

                      <div
                        className="alert alert-warning d-flex align-items-center mb-2"
                        role="alert"
                      >
                        <i className="ri-information-line me-2 fs-5"></i>
                        <span>
                          {t('tips.notice.payout')}
                          .
                        </span>
                      </div>

                      {/* ALERT O KWOTACH BRUTTO I PROWIZJI STRIPE */}
                      <div
                        className="alert alert-warning d-flex align-items-center mb-4"
                        role="alert"
                      >
                        <i className="ri-information-line me-2 fs-5"></i>
                        <span>
                          {t('tips.notice.stripeFees')}{' '}
                          <a
                            href="https://stripe.com/pricing"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="alert-link"
                          >
                            https://stripe.com/pricing
                          </a>
                          .
                        </span>
                      </div>

                      <div className="d-flex flex-wrap justify-content-between align-items-end mb-3 gap-3">
                        <div className="d-flex gap-2">
                          <div style={{ minWidth: '140px' }}>
                            <Label htmlFor="tipsYearFilter" className="form-label mb-1">
                              {t('tips.filter.year')}
                            </Label>
                            <Input
                              type="select"
                              id="tipsYearFilter"
                              value={selectedYear}
                              onChange={(e) => setSelectedYear(e.target.value)}
                            >
                              <option value="">{t('tips.filter.allYears')}</option>
                              {availableYears.map((y) => (
                                <option key={y} value={y}>{y}</option>
                              ))}
                            </Input>
                          </div>
                          <div style={{ minWidth: '160px' }}>
                            <Label htmlFor="tipsMonthFilter" className="form-label mb-1">
                              {t('tips.filter.month')}
                            </Label>
                            <Input
                              type="select"
                              id="tipsMonthFilter"
                              value={selectedMonth}
                              onChange={(e) => setSelectedMonth(e.target.value)}
                            >
                              <option value="">{t('tips.filter.allMonths')}</option>
                              {monthOptions.map((m) => (
                                <option key={m.value} value={m.value}>{m.label}</option>
                              ))}
                            </Input>
                          </div>
                        </div>
                        <div className="d-flex flex-wrap gap-4">
                          <div className="text-muted">
                            <span className="fw-semibold">{t('tips.status.unpaid')}:{" "}</span>
                            <span className="text-warning fs-5">{formatAmount(unpaidAmount)}</span>
                          </div>
                          <div className="text-muted">
                            <span className="fw-semibold">{t('tips.status.paid')}:{" "}</span>
                            <span className="text-success fs-5">{formatAmount(paidAmount)}</span>
                          </div>
                          <div className="text-muted">
                            <span className="fw-semibold">{t('tips.header.total')}:{" "}</span>
                            <span className="text-primary fs-5">{formatAmount(totalAmount)}</span>
                          </div>
                        </div>
                      </div>

                      {loading && tips.length === 0 ? (
                        <div className="text-center py-4">
                          <div
                            className="spinner-border text-primary"
                            role="status"
                          >
                            <span className="visually-hidden">
                              {t('tips.loading')}
                            </span>
                          </div>
                        </div>
                      ) : error ? (
                        <div className="alert alert-danger" role="alert">
                          <i className="ri-error-warning-line me-2"></i>
                          {t('tips.error')}: {error}
                        </div>
                      ) : filteredTips.length === 0 ? (
                        <div className="text-center py-4">
                          <div className="mb-3">
                            <i className="ri-money-dollar-circle-line display-4 text-muted"></i>
                          </div>
                          <h5 className="text-muted">
                            {tips.length === 0 ? t('tips.empty.title') : t('tips.empty.titleFiltered')}
                          </h5>
                          <p className="text-muted">
                            {tips.length === 0 ? t('tips.empty.description') : t('tips.empty.descriptionFiltered')}
                          </p>
                        </div>
                      ) : (
                        <TableContainer
                          columns={columns || []}
                          data={filteredTips || []}
                          customPageSize={10}
                          tableClass="table-centered align-middle table-nowrap mb-0"
                          theadClass="text-muted table-light"
                          SearchPlaceholder={t('tips.search.placeholder')}
                        />
                      )}
                    </TabPane>

                    <TabPane tabId="2">
                      {!arePayoutFieldsSaved() && (
                        <div className="alert alert-warning d-flex align-items-center mb-3" role="alert">
                          <i className="ri-information-line me-2 fs-5"></i>
                          <span>{t('Fill in payout data to enable tips')}</span>
                        </div>
                      )}
                      <div className="mb-4">
                        <div className="form-check form-switch form-switch-lg">
                          <Input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            id="allowTips"
                            onChange={handleAllowTipsChange}
                            checked={event?.allowTips || false}
                            disabled={!arePayoutFieldsSaved() || !arePayoutFieldsFilled()}
                          />
                          <Label className="form-check-label fs-5" htmlFor="allowTips">
                            {t('Allow Tips')}
                          </Label>
                        </div>
                      </div>

                      {profileUpdateSuccess && (
                        <Alert color="success" className="mb-3">
                          {t('Payout data saved successfully')}
                        </Alert>
                      )}
                      <Card>
                        <CardHeader>
                          <h6 className="card-title mb-0">{t('Payout Data')}</h6>
                        </CardHeader>
                        <CardBody>
                          <p className="text-muted mb-3">
                            {t('Provide bank account + recipient name, or a phone number for BLIK payout.')}
                          </p>
                          <Form onSubmit={payoutForm.handleSubmit}>
                            <Row>
                              <Col md={6}>
                                <div className="mb-3">
                                  <Label htmlFor="purchaser" className="form-label">
                                    {t('Recipient name')}
                                  </Label>
                                  <Input
                                    type="text"
                                    id="purchaser"
                                    name="purchaser"
                                    placeholder={t('Name and surname or company name')}
                                    value={payoutForm.values.purchaser}
                                    onChange={payoutForm.handleChange}
                                    onBlur={payoutForm.handleBlur}
                                  />
                                </div>
                              </Col>
                              <Col md={6}>
                                <div className="mb-3">
                                  <Label htmlFor="bankAccount" className="form-label">
                                    {t('Bank account number')}
                                  </Label>
                                  <Input
                                    type="text"
                                    id="bankAccount"
                                    name="bankAccount"
                                    placeholder={t('26 digits or PL + 26 digits')}
                                    value={payoutForm.values.bankAccount}
                                    onChange={payoutForm.handleChange}
                                    onBlur={payoutForm.handleBlur}
                                    invalid={payoutForm.touched.bankAccount && !!payoutForm.errors.bankAccount}
                                  />
                                  {payoutForm.touched.bankAccount && payoutForm.errors.bankAccount && (
                                    <FormFeedback>{payoutForm.errors.bankAccount}</FormFeedback>
                                  )}
                                </div>
                              </Col>
                            </Row>
                            <Row>
                              <Col md={6}>
                                <div className="mb-3">
                                  <Label htmlFor="phoneNumber" className="form-label">
                                    {t('Phone number (BLIK payout)')}
                                  </Label>
                                  <Input
                                    type="text"
                                    id="phoneNumber"
                                    name="phoneNumber"
                                    placeholder="+48 600 000 000"
                                    value={payoutForm.values.phoneNumber}
                                    onChange={payoutForm.handleChange}
                                    onBlur={payoutForm.handleBlur}
                                    invalid={payoutForm.touched.phoneNumber && !!payoutForm.errors.phoneNumber}
                                  />
                                  {payoutForm.touched.phoneNumber && payoutForm.errors.phoneNumber && (
                                    <FormFeedback>{payoutForm.errors.phoneNumber}</FormFeedback>
                                  )}
                                </div>
                              </Col>
                            </Row>
                            <div className="d-flex justify-content-end">
                              <button
                                type="submit"
                                className="btn btn-label btn-success"
                                disabled={profileLoading || (event?.allowTips && !arePayoutFieldsFilled())}
                              >
                                {profileLoading ? (
                                  <Spinner size="sm" />
                                ) : (
                                  <>
                                    <i className="ri-check-double-line label-icon align-middle fs-16 me-2"></i>
                                    {t('Save')}
                                  </>
                                )}
                              </button>
                            </div>
                          </Form>
                        </CardBody>
                      </Card>
                    </TabPane>
                  </TabContent>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default TipsListPage;
